import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X, ArrowRight } from "lucide-react";
import { SITE, NAV_LINKS, SERVICE_MENU } from "@/config/site";
import { EASE } from "@/components/Reveal";

const Logo = ({ light = false }) => (
  <Link to="/" className="flex items-center gap-3" data-testid="brand-logo" aria-label="NRI Property Services — Home">
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gold-500 font-display text-sm font-extrabold tracking-tight text-navy-900">
      NRI
    </div>
    <div className="leading-tight">
      <span className={`block font-display text-[15px] font-extrabold tracking-tight ${light ? "text-white" : "text-navy-900"}`}>
        NRI Property Services
      </span>
      <span className={`block text-[9px] font-semibold uppercase tracking-[0.2em] ${light ? "text-white/50" : "text-muted-foreground"}`}>
        A Quality Expertz Group Company
      </span>
    </div>
  </Link>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-[80] transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled ? "border-b border-navy-900/10 bg-white/85 backdrop-blur-xl" : "bg-transparent"
        }`}
      >
        <div className="container-site flex h-[76px] items-center justify-between">
          <Logo light={!scrolled} />

          <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
            <div className="group relative">
              <button
                className={`flex items-center gap-1.5 font-display text-[13px] font-bold tracking-wide transition-colors ${
                  scrolled ? "text-navy-900 hover:text-gold-600" : "text-white/90 hover:text-gold-400"
                }`}
                data-testid="nav-services-trigger"
                aria-haspopup="true"
              >
                Services <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180" />
              </button>
              <div className="invisible absolute left-1/2 top-full z-50 -translate-x-1/2 pt-5 opacity-0 transition-[opacity,transform] duration-300 group-hover:visible group-hover:opacity-100">
                <div className="grid w-[560px] grid-cols-2 gap-1 rounded-2xl border border-navy-900/10 bg-white p-3 shadow-[0_30px_70px_-20px_rgba(10,17,40,0.35)]">
                  {SERVICE_MENU.map((s) => (
                    <NavLink
                      key={s.href}
                      to={s.href}
                      data-testid={`nav-service-${s.href.slice(1)}`}
                      className="rounded-lg px-4 py-2.5 font-display text-[13px] font-semibold text-navy-900/80 transition-colors hover:bg-ivory hover:text-gold-600"
                    >
                      {s.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            </div>
            {NAV_LINKS.filter((l) => l.href !== "/services").map((l) => (
              <NavLink
                key={l.href}
                to={l.href}
                data-testid={`nav-${l.href.slice(1)}`}
                className={({ isActive }) =>
                  `font-display text-[13px] font-bold tracking-wide transition-colors ${
                    scrolled
                      ? isActive ? "text-gold-600" : "text-navy-900 hover:text-gold-600"
                      : isActive ? "text-gold-400" : "text-white/90 hover:text-gold-400"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/contact" className="btn-gold hidden !px-6 !py-3 lg:inline-flex" data-testid="nav-cta">
              Free Assessment <ArrowRight className="h-4 w-4" />
            </Link>
            <button
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-open"
              className={`flex h-11 w-11 items-center justify-center rounded-full border lg:hidden ${
                scrolled ? "border-navy-900/15 text-navy-900" : "border-white/25 text-white"
              }`}
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[100] flex flex-col bg-navy-950"
            data-testid="mobile-menu"
            role="dialog"
            aria-modal="true"
          >
            <div className="container-site flex h-[76px] items-center justify-between">
              <Logo light />
              <button
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-menu-close"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/25 text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="container-site flex flex-1 flex-col justify-center gap-1 overflow-y-auto py-6" aria-label="Mobile">
              {[...NAV_LINKS, { label: "NRI Tax & TDS Guide", href: "/nri-tax-tds-guide" }].map((l, i) => (
                <motion.div
                  key={l.href}
                  initial={{ opacity: 0, y: 26 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.06 * i + 0.1, duration: 0.6, ease: EASE }}
                >
                  <NavLink
                    to={l.href}
                    data-testid={`mobile-nav-${l.href.slice(1)}`}
                    className="group flex items-center justify-between border-b border-white/10 py-4"
                  >
                    <span className="font-display text-2xl font-extrabold tracking-tight text-white transition-colors group-hover:text-gold-400">
                      {l.label}
                    </span>
                    <ArrowRight className="h-5 w-5 text-gold-500 opacity-0 transition-opacity group-hover:opacity-100" />
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 26 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.6, ease: EASE }}
                className="mt-8"
              >
                <Link to="/contact" className="btn-gold w-full" data-testid="mobile-nav-cta">
                  Get a Free Property Assessment
                </Link>
                <p className="mt-6 text-center text-xs uppercase tracking-[0.25em] text-white/40">
                  Currently serving Hyderabad & Greater Hyderabad
                </p>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
